/**
 * API utility functions for making requests to the backend server
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/constants';

/**
 * Parse file list response (handles both JSON and text formats)
 * @param {Response} response - Fetch response object
 * @returns {Promise<string[]>} Array of file paths
 */
export async function parseFileListResponse(response) {
  const contentType = response.headers.get('content-type');
  let fileArray = [];
  
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();
    fileArray = data.files || data || [];
  } else {
    const text = await response.text();
    try {
      const data = JSON.parse(text);
      fileArray = data.files || data || [];
    } catch (e) {
      // If not JSON, treat as newline-separated file paths
      fileArray = text.trim().split('\n').filter(line => line.trim());
    }
  }
  
  return fileArray;
}

/**
 * Parse file content response (handles both JSON and text formats)
 * @param {Response} response - Fetch response object
 * @returns {Promise<string>} File content as string
 */
export async function parseFileContentResponse(response) {
  const contentType = response.headers.get('content-type');
  let fileContent = '';
  
  if (contentType && contentType.includes('application/json')) {
    const data = await response.json();
    fileContent = data.content || data || '';
  } else {
    const text = await response.text();
    try {
      const data = JSON.parse(text);
      fileContent = data.content || data || '';
    } catch (e) {
      // Not JSON, treat as plain text
      fileContent = text;
    }
  }
  
  return fileContent;
}

/**
 * Normalize path by removing root folder name
 * Server expects relative paths without root folder name
 * @param {string} path - File path (may include root folder name)
 * @returns {string} Normalized relative path
 */
export function normalizePath(path) {
  if (!path) return '';
  
  const pathParts = path.split('/');
  // Remove root folder if present (first part)
  return pathParts.length > 1 ? pathParts.slice(1).join('/') : path;
}

/**
 * Convert relative path to absolute path
 * @param {string} relativePath - Relative file path
 * @param {string} workspaceRoot - Workspace root directory
 * @param {string} rootFolderName - Root folder name to strip if present
 * @returns {string} Absolute file path
 */
export function toAbsolutePath(relativePath, workspaceRoot, rootFolderName) {
  if (!relativePath) return '';
  
  if (relativePath.startsWith('/')) {
    // Already absolute path
    return relativePath;
  }
  
  if (relativePath.startsWith(workspaceRoot)) {
    // Path already includes workspace root
    return relativePath;
  }
  
  // Remove root folder name from path if present
  let normalizedPath = relativePath;
  if (relativePath.startsWith(`${rootFolderName}/`)) {
    normalizedPath = relativePath.substring(rootFolderName.length + 1);
  }
  
  // Prepend workspace root
  return `${workspaceRoot}/${normalizedPath}`;
}

/**
 * Fetch file list from server
 * @returns {Promise<string[]>} Array of file paths
 */
export async function fetchFileList() {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LIST}`, {
    method: 'GET',
    mode: 'cors'
  });
  
  if (!response.ok) {
    throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
  }
  
  return parseFileListResponse(response);
}

/**
 * Fetch file content from server
 * @param {string} path - File path (will be normalized)
 * @returns {Promise<string>} File content
 */
export async function fetchFileContent(path) {
  const normalizedPath = normalizePath(path);
  const encodedPath = encodeURIComponent(normalizedPath);

  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.READ}?path=${encodedPath}`, {
    method: 'GET',
    mode: 'cors'
  });

  if (!response.ok) {
    throw new Error(`Failed to read file: ${response.status} ${response.statusText}`);
  }

  return parseFileContentResponse(response);
}

/**
 * Fetch file content using exact path (no normalization)
 * @param {string} rawPath - Exact relative path from project root
 * @returns {Promise<string>} File content
 */
export async function fetchFileRaw(rawPath) {
  const encodedPath = encodeURIComponent(rawPath);

  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.READ}?path=${encodedPath}`, {
    method: 'GET',
    mode: 'cors'
  });

  if (!response.ok) {
    throw new Error(`Failed to read file: ${response.status} ${response.statusText}`);
  }

  return parseFileContentResponse(response);
}

/**
 * Check server status
 * @returns {Promise<boolean>} True if server is active
 */
export async function checkServerStatus() {
  try {
    const response = await fetch(`${API_BASE_URL}/`, {
      method: 'GET',
      mode: 'cors'
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Detect Claude config (CLAUDE.md) and skills (.claude/skills/*.md)
 * @returns {Promise<Object>} Object with claudeMd and skills array
 */
export async function detectClaude() {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.DETECT_CLAUDE}`, {
    method: 'GET',
    mode: 'cors'
  });

  if (!response.ok) {
    throw new Error(`Detect Claude failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Write a file to disk
 * @param {string} filePath - Relative file path (e.g. '.claude/skills/my-skill.md')
 * @param {string} content - File content
 * @returns {Promise<Object>} { status: 'ok' }
 */
export async function writeFile(filePath, content) {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.WRITE}`, {
    method: 'POST',
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: filePath, content })
  });

  if (!response.ok) {
    throw new Error(`Write failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a file from disk
 * @param {string} filePath - Relative file path
 * @returns {Promise<Object>} { status: 'ok' }
 */
export async function deleteFile(filePath) {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.DELETE}`, {
    method: 'POST',
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: filePath })
  });

  if (!response.ok) {
    throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Scan codebase for architectural standards violations
 * @param {string} projectPath - Project root path
 * @param {string} standards - Standards text to check against
 * @returns {Promise<Object>} Object with violations array
 */
export async function scanStandards(projectPath, standards) {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SCAN_STANDARDS}`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      projectPath,
      standards
    })
  });
  
  if (!response.ok) {
    throw new Error(`Standards scan failed: ${response.status} ${response.statusText}`);
  }
  
  const result = await response.json();
  return result;
}
